import { DealerState } from '@appTypes/DealerState';
import { IPitchStatusState, IPitchSubStatus } from '@appTypes/PitchStatus';
import darkTip from '@assets/svgs/dark-tip.svg';
import { useDealStatus } from '@hooks/UseDealStatus';
import { useMobileTabletDesktop } from '@hooks/dealsheet/UseMobileTabletDesktop';
import { useOutsideClickDetection } from '@hooks/UseOutsideClickDetection';
import { AppState } from '@store/AppState';
import { theme } from '@styles/theme';
import { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Tooltip from '../tooltip/Tooltip';

type OpenStatusState = {
  selected: number;
  current?: number;
};

type PitchStatusProps = {
  selectedStatus: IPitchStatusState;
  pitchStatusChange: (status: IPitchStatusState) => void;
  fromNotesModal?: boolean;
  position?: string;
  useButton?: boolean;
};

const PitchStatusDropdown: FC<PitchStatusProps> = ({
  selectedStatus,
  pitchStatusChange,
  fromNotesModal,
  position,
  useButton,
}) => {
  const device = useMobileTabletDesktop();
  const isDesktop = device === 'desktop';

  // keep track of current and previously selected status in dropdown.
  const [openStatus, setOpenStatusState] = useState<OpenStatusState>({
    selected: selectedStatus.index,
  });

  // keep track of pitch status dropdown open state.
  const [dropdownOpen, setDropdownOpenState] = useState<boolean>(false);

  // hook to get color code associated with status.
  const { getColor } = useDealStatus();

  // get access of pitch-status-list from store.
  const { pitchStatus } = useSelector<AppState, DealerState>((state) => state.dealer);

  // keep reference of current dropdown to track outside click.
  const wrapperRef = useRef<HTMLDivElement>(null);

  // keep track of selectedStatus internally whenever props.selectedStatus changed.
  useEffect(() => {
    setOpenStatusState({ selected: selectedStatus.index });
  }, [selectedStatus]);

  /*
    check if current status or subStaus is selected staus or not
    @Input:  current status, level (1: primary status, 2 or null: subStatus)
    @Output: true or false
  */
  const isSelected = (current: IPitchStatusState | IPitchSubStatus, level?: number) => {
    if (level === 1) {
      return selectedStatus.index === current.index;
    } else {
      return selectedStatus?.subStatuses[0]?.index === current.index;
    }
  };

  // keep track of opened status in dropdown by changing openStaus.current state.
  const onOpenStatus = (current: IPitchStatusState) => {
    if (!(current.index === openStatus.selected)) {
      setOpenStatusState({ ...openStatus, current: current.index });
    }
  };

  const onPitchStatusChange = (status: IPitchStatusState, subStatus: IPitchSubStatus) => {
    setDropdownOpenState(false);
    const selectedPitchStatus = {
      ...status,
      subStatuses: [subStatus],
    };
    pitchStatusChange(selectedPitchStatus);
  };

  const getTooltipContent = () => {
    return selectedStatus.subStatuses.length === 0 ? null : (
      <span>{selectedStatus?.subStatuses[0]?.name}</span>
    );
  };

  useOutsideClickDetection(wrapperRef, setDropdownOpenState);

  return (
    <PitchStatusDropdownWrapper
      ref={wrapperRef}
      selectedStatusColor={() => getColor(selectedStatus?.name)}
      fromNotesModal={fromNotesModal}
      isDesktop={isDesktop}
    >
      <>
        {useButton && (
          <>
            <Tooltip
              content={getTooltipContent()}
              trigger={
                <button
                  onClick={() => setDropdownOpenState(!dropdownOpen)}
                  style={{ background: getColor(selectedStatus?.name) }}
                >
                  <span className='status-name'>{selectedStatus?.name}</span>
                  <i className='am-icon-angle-down' />
                </button>
              }
              position={position || 'top'}
            />
          </>
        )}

        {!useButton && (
          <div
            className='status-dropdown-wrapper'
            onClick={() => setDropdownOpenState(!dropdownOpen)}
          >
            <Tooltip
              content={getTooltipContent()}
              trigger={
                <span
                  className='status-name'
                  style={{ background: getColor(selectedStatus?.name) }}
                >
                  {selectedStatus?.name}
                  <span className='am-icon am-icon-angle-down'></span>
                </span>
              }
              position={position || 'top'}
            />
          </div>
        )}
      </>

      {dropdownOpen && (
        <div className={`status-popover ${useButton ? 'with-btn' : ''}`}>
          <div className='popover-tip'></div>
          <label>Change Status</label>
          <hr></hr>
          {pitchStatus?.map((status) => {
            return (
              <ul key={status.index} className='pitch-status-options-wrapper'>
                <li
                  onClick={() => onOpenStatus(status)}
                  style={{
                    borderLeftColor: getColor(status.name),
                  }}
                  className='level-1-option box-model border-left-5'
                >
                  <span
                    className='pitch-status-text'
                    style={{
                      backgroundColor: isSelected(status, 1) ? getColor(status.name) : '',
                    }}
                  >
                    {status.name}
                    <em>…</em>
                  </span>
                  {(openStatus.selected === status.index ||
                    openStatus.current === status.index) && (
                      <ul className='level-2-option-wrapper box-model'>
                        {status.subStatuses.map((subStatus) => {
                          return (
                            <li
                              key={subStatus.index}
                              onClick={() => onPitchStatusChange(status, subStatus)}
                              style={{
                                borderLeftColor: !isSelected(subStatus) && getColor(status.name),
                                listStyleType: 'none',
                              }}
                              className={
                                'level-2-option box-model border-left-3' +
                                (isSelected(subStatus)
                                  ? ' selected-subStatus'
                                  : ' not-selected-subStatus')
                              }
                            >
                              <span className='pitch-status-text'>{subStatus.name}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                </li>
              </ul>
            );
          })}
        </div>
      )}
    </PitchStatusDropdownWrapper>
  );
};

export default PitchStatusDropdown;

type WrapperProps = {
  selectedStatusColor: () => string;
  fromNotesModal?: boolean;
  isDesktop?: boolean;
};

const PitchStatusDropdownWrapper = styled.div<WrapperProps>`
  position: relative;

  button {
    align-items: center;
    border: 0;
    border-radius: 20px;
    color: white;
    cursor: pointer;
    display: flex;
    font-size: $text-md-size;
    font-weight: $weight-regular;
    padding: 5px 15px;
    position: relative;
    white-space: nowrap;
    user-select: none;

    span.status-name {
      padding-right: 5px;
      font-size: 14px;
    }
  }

  .status-dropdown-wrapper {
    float: left;
    margin-left: 14px;
    font-size: ${theme.typography.h5}px;
    cursor: pointer;
    position: relative;
    top: ${(props) => (props?.fromNotesModal ? '2px;' : '4px;')} .am-icon {
      font-size: 120%;
      padding: 5px;
    }

    .status-name {
      color: white;
      border-radius: 20px;
      padding: 5px 6px 5px 10px;
      position: relative;
      ${(props) =>
    props?.fromNotesModal &&
    `outline: 2px solid #412662;
          &:hover {
            filter: brightness(90%);
          }`}
    }
  }

  .box-model {
    position: relative;
    margin: 2px -1px;
    cursor: pointer;
    line-height: 20px;
    border-left-style: solid;
    padding: 0;
  }

  .status-popover {
    position: absolute;
    left: 52%;
    top: 44px;
    right: auto;
    width: 160px;
    z-index: 130;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.25);
    background: #333;
    border-radius: 4px;
    margin-top: 0;
    padding: 12px 20px;
    box-sizing: content-box;
    margin-left: -100px;
    ${(props) =>
    props?.isDesktop &&
    `max-height: 340px;
        overflow-x: hidden;

        ::-webkit-scrollbar {
          width: 10px;
        }

        ::-webkit-scrollbar-thumb {
          background-color: #888;
          border-radius: 5px;
        }

        ::-webkit-scrollbar-track {
          background-color: #333;
        }`}

    .pitch-status-text {
      color: #e8e8e8;
      display: block;
      padding: 3px 0 3px 7px;
    }

    .popover-tip {
      background: url(${darkTip}) no-repeat 50%;
      top: -16px;
      transform: rotate(0deg);
      display: block;
      position: absolute;
      width: 64px;
      height: 17px;
      overflow: hidden;
      margin-left: 50px;
      background-size: 88px 19px;
    }

    &.with-btn .popover-tip {
      background-size: initial;
      top: -15px;
    }

    label {
      line-height: 1;
      color: #fff;
      margin-top: -10px;
      margin-bottom: 10px;
      font-weight: 600;
    }

    hr {
      border: 0;
      border-top: 1px solid #272727;
      box-shadow: inset 0 1px 0 #404040;
      margin: 0 -20px;
      height: 1px;
    }

    .pitch-status-options-wrapper {
      border-top: 0;
      background-color: #333;
      width: 100%;
      margin: 0;
      padding: 1px 0 0;
      list-style: none outside none;
    }

    .level-2-option-wrapper {
      border-top: 0;
      background-color: #333;
      margin-left: -5px;
      padding: 1px 0 0 16px;
      margin-top: 0px;
      border-left-width: 0;

      .not-selected-subStatus:hover {
        background-color: #e8e8e8;

        .pitch-status-text {
          color: #333;
        }
      }
    }

    .selected-subStatus {
      font-weight: 600;
      border-left-width: 0;
    }

    .selected-subStatus::before {
      font-size: 14px;
      font-family: icomoon;
      content: '';
      position: absolute;
      border-style: solid;
      border-width: 0 3px 3px 0;
      border-radius: 3px;
      height: 12px;
      top: 5px;
      left: -5px;
      transform: rotate(45deg);
      width: 8px;
      border-color: ${(props) => props?.selectedStatusColor};
    }
  }
`;
